<template>
  <div class="intercom-layout">
    <!-- Topbar -->
    <header class="intercom-layout-topbar">
      <slot name="topbar" />
    </header>

    <!-- Main Layout (Sidebar + Content) -->
    <div class="intercom-layout-main">
      <!-- Sidebar -->
      <aside
        v-if="$slots.sidebar"
        :class="sidebarClasses"
      >
        <slot name="sidebar" />
      </aside>

      <!-- Main Content -->
      <main class="intercom-layout-content">
        <slot />
      </main>
    </div>

    <!-- Mobile Sidebar Overlay -->
    <div
      v-if="$slots.sidebar && isSidebarOpen"
      class="intercom-layout-overlay"
      @click="closeSidebar"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const isSidebarOpen = ref(false)

const sidebarClasses = computed(() => [
  'intercom-layout-sidebar',
  {
    'intercom-layout-sidebar--open': isSidebarOpen.value,
  },
])

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const closeSidebar = () => {
  isSidebarOpen.value = false
}

const openSidebar = () => {
  isSidebarOpen.value = true
}

defineExpose({
  toggleSidebar,
  closeSidebar,
  openSidebar,
  isSidebarOpen,
})
</script>

<style scoped>
/* ========================================
   INTERCOM LAYOUT BASE
   ======================================== */
.intercom-layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--color-white);
}

/* ========================================
   TOPBAR
   ======================================== */
.intercom-layout-topbar {
  height: var(--topbar-height);
  flex-shrink: 0;
  z-index: var(--z-fixed);
}

/* ========================================
   MAIN LAYOUT
   ======================================== */
.intercom-layout-main {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* ========================================
   SIDEBAR
   ======================================== */
.intercom-layout-sidebar {
  width: var(--sidebar-width);
  background: var(--color-white);
  border-right: 1px solid var(--color-gray-200);
  flex-shrink: 0;
  overflow-y: auto;
  z-index: var(--z-sticky);
}

/* ========================================
   MAIN CONTENT
   ----------------------------------------
   Owns page chrome (padding) so every view gets correct spacing
   by default, with or without a page-wrapper class. Views never
   redefine padding on their wrapper -- they only opt into width
   control via .is-narrow if they need a centered narrow layout.
   ======================================== */
.intercom-layout-content {
  flex: 1;
  overflow-y: auto;
  background: var(--color-gray-50);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .intercom-layout-content {
    padding: var(--space-4);
  }
}

/* ========================================
   MOBILE RESPONSIVE
   ======================================== */
@media (max-width: 768px) {
  .intercom-layout-sidebar {
    position: fixed;
    top: var(--topbar-height);
    left: 0;
    bottom: 0;
    transform: translateX(-100%);
    transition: transform var(--transition-base);
    box-shadow: var(--shadow-lg);
    z-index: var(--z-modal);
  }

  .intercom-layout-sidebar--open {
    transform: translateX(0);
  }

  .intercom-layout-overlay {
    position: fixed;
    top: var(--topbar-height);
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: var(--z-modal-backdrop);
    animation: fadeIn 0.2s ease-out;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
}
</style>
